export default function ResponsibleDisclosurePage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Responsible Disclosure Policy
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            Last Updated: 12 April 2026
          </p>
        </div>
      </div>

      {/* 1. Our Commitment */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">1. Our Commitment</h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                At CyberActions, the security of our platform and customer data is our highest priority.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                We value the work of the global security community and believe that responsible
                vulnerability disclosure plays a critical role in maintaining a safe and secure ecosystem.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Scope */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">2. Scope</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              This policy applies to the following systems:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>cyberactions.co.uk</li>
              <li>All associated subdomains (including portal.cyberactions.co.uk)</li>
              <li>Public-facing APIs and application endpoints</li>
            </ul>
            <h3 className="mt-8 text-xl font-semibold text-slate-900 dark:text-white">Out of Scope</h3>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The following are not covered under this policy:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Third-party services and platforms (e.g., Microsoft 365, Google Workspace)</li>
              <li>Infrastructure providers or external hosting environments</li>
              <li>Issues requiring physical access</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 3. Safe Harbour */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">3. Safe Harbour</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              If you conduct security research in accordance with this policy:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>We consider your actions authorised</li>
              <li>We will not pursue legal action against you</li>
              <li>We will not support legal claims against you</li>
            </ul>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              This applies provided that:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>You act in good faith</li>
              <li>You avoid privacy violations and service disruption</li>
              <li>You comply with applicable laws, including the UK Computer Misuse Act 1990</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 4. How to Report a Vulnerability */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              4. How to Report a Vulnerability
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Please report vulnerabilities to:{' '}
              <a
                href="mailto:security@cyberactions.co.uk"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                security@cyberactions.co.uk
              </a>
            </p>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              To help us triage efficiently, include:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Description of the issue and its impact</li>
              <li>Steps to reproduce</li>
              <li>Affected asset (URL, endpoint, system)</li>
              <li>Proof of Concept (PoC) (non-destructive only)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 5. Responsible Research Guidelines */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              5. Responsible Research Guidelines
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              To protect our users and platform, you must:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Avoid accessing, modifying, or deleting customer data</li>
              <li>Avoid actions that degrade service performance (e.g., DoS attacks)</li>
              <li>Stop immediately if you encounter personal or sensitive data</li>
              <li>Maintain confidentiality until the issue is resolved</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 6. Out-of-Scope Vulnerabilities */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              6. Out-of-Scope Vulnerabilities
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The following are generally not accepted unless they lead to a significant security impact:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Clickjacking on non-sensitive pages</li>
              <li>Missing or misconfigured SPF, DKIM, or DMARC records</li>
              <li>Missing HTTP security headers (unless exploitable)</li>
              <li>Login/logout CSRF without demonstrated impact</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 7. Our Commitment to You */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              7. Our Commitment to You
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              When you report a valid vulnerability, we will:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Acknowledge your report within 48 hours</li>
              <li>Provide status updates during investigation</li>
              <li>Work to remediate issues within a reasonable timeframe based on severity</li>
              <li>Provide public recognition (with your consent)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 8. Disclosure Policy */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              8. Disclosure Policy
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">We ask that you:</p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Do not publicly disclose vulnerabilities until we have resolved them</li>
              <li>Allow us reasonable time to investigate and fix the issue</li>
            </ul>
            <p className="mt-6 text-slate-600 dark:text-slate-300">
              We support coordinated disclosure practices.
            </p>
          </div>
        </div>
      </div>

      {/* 9. No Bug Bounty Programme */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              9. No Bug Bounty Programme
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              CyberActions does not currently offer monetary rewards for vulnerability disclosures.
            </p>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We greatly appreciate responsible reporting and may provide public recognition where
              appropriate.
            </p>
          </div>
        </div>
      </div>

      {/* 10. Legal Compliance */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              10. Legal Compliance
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              All research must comply with applicable laws and regulations, including:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>The UK Computer Misuse Act 1990</li>
              <li>Data protection laws, including UK GDPR</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Contact</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              For security-related inquiries, contact{' '}
              <a
                href="mailto:security@cyberactions.co.uk"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                security@cyberactions.co.uk
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
